/**
 * RoleSwitcher — header widget that shows the active role and, when
 * the user holds multiple roles in the active org, opens a dropdown
 * to switch hats without signing out.
 *
 * Single-role users see no UI at all (no chevron, no label, no churn)
 * — the visual footprint is zero for the 95% of users who only ever
 * hold one role. Multi-role users (Cicely, Maria after M4) see a
 * compact pill showing the active hat; clicking opens a dropdown of
 * every role they hold in this org, with the current one checked.
 *
 * Pattern mirrors OrgSwitcher: switch-role Edge Function call →
 * refreshSession → queryClient.clear → window.location.assign(/app/
 * dashboard), so the new active hat takes effect cleanly under a
 * fresh JWT with no in-memory state from the prior role bleeding
 * across — and on a URL the new hat can actually read.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation }                               from 'react-i18next'
import { useUserRolesForOrg }                           from '@/hooks/useUserRolesForOrg'
import { useSwitchRole }                                from '@/hooks/useSwitchRole'
import { useAuthStore }                                 from '@/store/authStore'
import { isAppError }                                   from '@/lib/errors/AppError'

export interface RoleSwitcherProps {
  /** "compact" for mobile header, "full" for sidebar. */
  variant?: 'compact' | 'full'
}

const ROLE_LABEL_KEYS: Record<string, { key: string; fallback: string }> = {
  super_admin:      { key: 'roleSwitcher.role.super_admin',      fallback: 'Super admin' },
  contractor_admin: { key: 'roleSwitcher.role.contractor_admin', fallback: 'Admin' },
  supervisor:       { key: 'roleSwitcher.role.supervisor',       fallback: 'Supervisor' },
  cleaner:          { key: 'roleSwitcher.role.cleaner',          fallback: 'Cleaner' },
  maintenance:      { key: 'roleSwitcher.role.maintenance',      fallback: 'Maintenance' },
  client_view:      { key: 'roleSwitcher.role.client_view',      fallback: 'Client view' },
}

export function RoleSwitcher({ variant = 'full' }: RoleSwitcherProps): ReactNode {
  const { t }     = useTranslation()
  const grants    = useUserRolesForOrg()
  const switchHat = useSwitchRole()
  const activeRole = useAuthStore((s) => {
    const meta = s.user?.app_metadata as { role?: string } | undefined
    return meta?.role ?? null
  })

  function labelFor(role: string): string {
    const lookup = ROLE_LABEL_KEYS[role]
    return lookup ? t(lookup.key, lookup.fallback) : role
  }

  const [open, setOpen] = useState(false)
  const containerRef    = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const rows = grants.data ?? []
  // De-dup defensively (PK on the table prevents real dupes; this guards
  // against a stale cache or test fixture). Sort by display label so the
  // dropdown order is stable across mounts.
  const uniqueRoles = Array.from(new Set(rows.map((r) => r.role)))
  const isMulti     = uniqueRoles.length > 1
  const errMsg = switchHat.error && isAppError(switchHat.error) ? switchHat.error.userMessage : null

  function handlePick(role: string): void {
    setOpen(false)
    if (role === activeRole) return
    void switchHat.mutateAsync({ targetRole: role })
  }

  // Single-role users see nothing. The visual matches the previous
  // "no role widget" rendering so single-role tenants are unaffected.
  if (!isMulti) return null

  return (
    <div
      ref={containerRef}
      data-testid="role-switcher"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="role-switcher-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={switchHat.isPending}
        style={{
          background:    '#ffffff',
          border:        '1px solid #1f2937',
          color:         '#111827',
          borderRadius:  '999px',
          padding:       variant === 'compact' ? '0.2rem 0.55rem' : '0.3rem 0.7rem',
          fontSize:      variant === 'compact' ? '0.8rem' : '0.85rem',
          fontWeight:    600,
          cursor:        switchHat.isPending ? 'wait' : 'pointer',
          display:       'inline-flex',
          alignItems:    'center',
          gap:           '0.35rem',
        }}
      >
        <span data-testid="role-switcher-active-label">
          {switchHat.isPending
            ? t('roleSwitcher.switching', 'Switching…')
            : (activeRole ? labelFor(activeRole) : t('roleSwitcher.noRole', 'No role'))}
        </span>
        <span aria-hidden="true" style={{ fontSize: '0.65rem', opacity: 0.7 }}>▾</span>
      </button>

      {open && (
        <ul
          role="listbox"
          data-testid="role-switcher-list"
          style={{
            position:        'absolute',
            top:             'calc(100% + 0.25rem)',
            right:           0,
            zIndex:          50,
            minWidth:        '11rem',
            margin:          0,
            padding:         '0.3rem 0',
            background:      '#ffffff',
            color:           '#111827',
            border:          '1px solid #d1d5db',
            borderRadius:    '6px',
            boxShadow:       '0 6px 18px rgba(0,0,0,0.18)',
            listStyle:       'none',
          }}
        >
          {uniqueRoles.map((role) => {
            const selected = role === activeRole
            return (
              <li
                key={role}
                role="option"
                aria-selected={selected}
                data-testid={`role-switcher-option-${role}`}
                onClick={() => handlePick(role)}
                style={{
                  padding:       '0.55rem 0.85rem',
                  cursor:        'pointer',
                  fontSize:      '0.95rem',
                  color:         '#111827',
                  background:    selected ? '#eef2ff' : '#ffffff',
                  display:       'flex',
                  alignItems:    'center',
                  justifyContent:'space-between',
                  gap:           '0.5rem',
                }}
              >
                <span style={{ fontWeight: selected ? 600 : 400 }}>{labelFor(role)}</span>
                {selected && (
                  <span aria-hidden="true" style={{ color: 'var(--brand-primary)', fontSize: '0.85rem' }}>✓</span>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {errMsg && (
        <p
          role="alert"
          data-testid="role-switcher-error"
          style={{
            position:    'absolute',
            top:         'calc(100% + 0.25rem)',
            right:       0,
            margin:      0,
            padding:     '0.25rem 0.5rem',
            background:  'var(--brand-danger)',
            color:       'var(--brand-white)',
            fontSize:    '0.75rem',
            borderRadius:'var(--brand-radius-sm)',
            whiteSpace:  'nowrap',
          }}
        >
          {errMsg}
        </p>
      )}
    </div>
  )
}
